import { Match, Show, Switch, createSignal } from "solid-js";
import type { ScheduleVoyage, Voyage, VoyageDetails } from "../../shared/api/types";
import { TextTooltip } from "../../shared/inputs/TextTooltip";
import { isHiddenStopVisible } from "../calendar/icon-position";
import { BasicSection } from "./BasicSection";
import { ItinerarySection } from "./ItinerarySection";
import { isScheduleVoyage } from "./modal-voyage";
import { StatusSection } from "./StatusSection";
import type { AdjacentState } from "./store";
import s from "./DetailsBody.module.css";

// Body of the voyage details popup. Voyage details + Voyage status are
// stacked in the left column; Itinerary takes its own column on the
// right (it's the tallest piece and benefits from the vertical space).
// All data flows in via props so the component owns no fetching state.

export interface DetailsBodyProps {
  voyage: ScheduleVoyage | Voyage;
  details: VoyageDetails | null;
  currentError: string | null;
  prevAdjacent: AdjacentState | null;
  nextAdjacent: AdjacentState | null;
  portRefs: Record<string, string>;
  portColors: Record<string, string>;
  onVoyageRefClick: (vesselCode: string, voyageNo: string) => void;
}

export function DetailsBody(props: DetailsBodyProps) {
  // Toggle lives here (not inside ItinerarySection) so it can sit on
  // the right of the section title row alongside the "Itinerary
  // (N stops)" heading.
  const [showAllStops, setShowAllStops] = createSignal(false);
  return (
    <div class={s.body}>
      <div class={s.leftStack}>
        <div class={`${s.col} ${s.colBasic}`}>
          <div class="details-section-title">Voyage details</div>
          <BasicSection
            details={props.details}
            placeholder={props.voyage}
            portRefs={props.portRefs}
            stopContext={(() => {
              // Calendar selections carry portName + arrival + departure;
              // vessels selections don't. Used to add the "Selected port"
              // / "Arrival" / "Departure" addendum for calendar context.
              const v = props.voyage;
              if (!isScheduleVoyage(v) || !v.arrivalLocal || !v.departureLocal) return null;
              return {
                portName: v.portName,
                arrivalLocal: v.arrivalLocal,
                departureLocal: v.departureLocal,
              };
            })()}
          />
        </div>
        <div class={`${s.col} ${s.colStatus}`}>
          <div class="details-section-title">Voyage status</div>
          <Switch fallback={<div class="muted">Loading status…</div>}>
            <Match when={props.currentError}>
              <div class={s.error}>
                Failed to load details: {props.currentError}
              </div>
            </Match>
            <Match when={props.details}>
              {(d) => (
                <StatusSection
                  details={d()}
                  prevAdjacent={props.prevAdjacent}
                  nextAdjacent={props.nextAdjacent}
                  onVoyageRefClick={props.onVoyageRefClick}
                />
              )}
            </Match>
          </Switch>
        </div>
      </div>
      <div class={`${s.col} ${s.colItinerary}`}>
        <div class="details-section-title">
          Itinerary
          <Show when={props.details?.itineraries?.length}>
            {(n) => {
              const visible = () => {
                if (showAllStops()) return n();
                return (props.details?.itineraries ?? [])
                  .filter((stp) => isHiddenStopVisible(stp.portFunc)).length;
              };
              // Show "V/N" only when stops are actually being hidden —
              // matching counts on both sides reads as noise.
              return (
                <span class="muted">
                  ({visible() === n() ? n() : `${visible()}/${n()}`} stops)
                </span>
              );
            }}
          </Show>
          <Show when={props.details}>
            <TextTooltip
              text={showAllStops() ? "Hide transit stops" : "Show all stops"}
              description={
                showAllStops()
                  ? "Only show: Load, Discharge, Pilot in/out, Commence voyage."
                  : "Reveal every itinerary entry."
              }
            >
              <button
                type="button"
                class={s.itineraryToggle}
                onClick={() => setShowAllStops(!showAllStops())}
                aria-pressed={showAllStops()}
              >
                {showAllStops() ? "Hide transit stops" : "Show all stops"}
              </button>
            </TextTooltip>
          </Show>
        </div>
        <Switch fallback={<div class="muted">Loading itinerary…</div>}>
          <Match when={props.currentError}>
            {/* Error is already surfaced in the status column. */}
            {null}
          </Match>
          <Match when={props.details}>
            {(d) => {
              // Calendar selections carry portName + per-call window;
              // vessels selections don't. The window narrows itinerary
              // highlighting to one stop when the vessel visits the
              // same terminal more than once in this voyage.
              const v = props.voyage;
              const isCal = isScheduleVoyage(v);
              return (
                <ItinerarySection
                  details={d()}
                  currentPortName={isCal ? v.portName : null}
                  currentArrivalLocal={isCal ? v.arrivalLocal : null}
                  currentDepartureLocal={isCal ? v.departureLocal : null}
                  portColors={props.portColors}
                  showAllStops={showAllStops()}
                />
              );
            }}
          </Match>
        </Switch>
      </div>
    </div>
  );
}
